import { isNullish } from '@sas/nova-core/typeguards';
import { Localizer } from '../../../../../../../locale/useTextLocalizer';

const billions = 'formatNumber.b.msg';
const millions = 'formatNumber.m.msg';
const thousands = 'formatNumber.k.msg';

// const defaultSASFormat = 'BEST8.';
export const timezoneUTC = 'UTC';

const integerFormat = (formatLocale: string) =>
  new Intl.NumberFormat(formatLocale, { maximumFractionDigits: 0 });

const numberFormat = (formatLocale: string, maxFractionDigits = 1) =>
  new Intl.NumberFormat(formatLocale, { maximumFractionDigits: maxFractionDigits });

export const formatInteger = (props: {
  formatLocale: string;
  value: number | undefined | null;
  defaultFormattedValue?: string;
}) => {
  const { formatLocale, value, defaultFormattedValue } = props;
  if (isNullish(value) || isNaN(value)) return defaultFormattedValue;
  return integerFormat(formatLocale).format(value);
};

export const formatIntegerPercent = (props: {
  formatLocale: string;
  localize: Localizer;
  value: number | undefined | null;
  defaultFormattedValue?: string;
}) => {
  const { formatLocale, value, defaultFormattedValue, localize } = props;
  if (isNullish(value) || isNaN(value)) return defaultFormattedValue;
  return localize('value.percent.msg', {
    percent:
      formatInteger({ formatLocale, value, defaultFormattedValue }) || defaultFormattedValue || '',
  });
};

export const formatPercent = (props: {
  formatLocale: string;
  localize: Localizer;
  value: number | undefined | null;
  maxFractionDigits?: number;
  defaultFormattedValue?: string;
}) => {
  const {
    localize,
    formatLocale,
    value,
    maxFractionDigits = 1,
    defaultFormattedValue = '',
  } = props;
  if (isNullish(value) || isNaN(value)) return defaultFormattedValue;
  return localize('value.percent.msg', {
    percent: numberFormat(formatLocale, maxFractionDigits).format(value),
  });
};

export const formatBoolean = (props: {
  localize: Localizer;
  value: boolean | undefined | null;
  defaultFormattedValue?: string;
}) => {
  const { localize, value, defaultFormattedValue } = props;
  if (isNullish(value)) return defaultFormattedValue ? defaultFormattedValue : '';
  return localize(`boolean.${value}.msg`);
};

export const formatNumber = (props: {
  formatLocale: string;
  localize: Localizer;
  value: number | undefined | null;
  defaultFormattedValue?: string;
}) => {
  const { formatLocale, localize, value, defaultFormattedValue } = props;
  const ranges = [
    { divider: 1e18, siSymbol: 'E', abbrevKey: '' },
    { divider: 1e15, siSymbol: 'P', abbrevKey: '' },
    { divider: 1e12, siSymbol: 'T', abbrevKey: '' },
    { divider: 1e9, siSymbol: 'G', abbrevKey: billions },
    { divider: 1e6, siSymbol: 'M', abbrevKey: millions },
    { divider: 1e3, siSymbol: 'k', abbrevKey: thousands },
  ];

  if (isNullish(value) || isNaN(value)) return defaultFormattedValue ? defaultFormattedValue : '';
  for (let i = 0; i < ranges.length; i++) {
    if (value >= ranges[i].divider) {
      const rVal = numberFormat(formatLocale).format(value / ranges[i].divider);
      if (ranges[i].abbrevKey.length > 0) {
        return localize(ranges[i].abbrevKey, { value: rVal });
      } else {
        return rVal;
      }
    }
  }
  return numberFormat(formatLocale).format(value);
};

// DSD+ start
export const formatNumberNoRange = (props: {
  formatLocale: string;
  value: number | undefined | null;
  maxFractionDigits: number;
  defaultFormattedValue: string;
}) => {
  const { formatLocale, value, maxFractionDigits = 1, defaultFormattedValue = '' } = props;
  if (isNullish(value) || isNaN(value)) return defaultFormattedValue;
  return numberFormat(formatLocale, maxFractionDigits).format(value);
};
// DSD+ end

export const formatBytes = (props: {
  formatLocale: string;
  localize: Localizer;
  bytes: number | undefined | null;
  defaultFormattedValue?: string;
}): string => {
  const { formatLocale, localize, bytes, defaultFormattedValue } = props;
  if (isNullish(bytes) || isNaN(bytes) || bytes < 0)
    return defaultFormattedValue ? defaultFormattedValue : '';

  const unitOfMeasures = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
  const k = 1024;

  const toLocaleString = (size: number, unitOfMeasure: string): string => {
    const key =
      size === 1 && unitOfMeasure === 'bytes'
        ? `formatBytes.byte.msg`
        : `formatBytes.${unitOfMeasure}.msg`;
    const sz = numberFormat(formatLocale).format(size);
    return localize(key, { value: sz });
  };

  if (bytes === 0) return toLocaleString(0, 'bytes');

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return toLocaleString(size, unitOfMeasures[i]);
};

export const formatRawBytes = (props: {
  formatLocale: string;
  localize: Localizer;
  bytes: number | undefined | null;
  defaultFormattedValue?: string;
}): string => {
  const { formatLocale, localize, bytes, defaultFormattedValue } = props;
  if (isNullish(bytes) || isNaN(bytes) || bytes < 0)
    return defaultFormattedValue ? defaultFormattedValue : '';
  const key = bytes === 1 ? 'formatBytes.byte.msg' : 'formatBytes.bytes.msg';
  const sz = integerFormat(formatLocale).format(bytes);
  return localize(key, { value: sz });
};

export const valueToDate = (value: any): Date | null => {
  if (Object.prototype.toString.call(value) === '[object Date]') {
    return value as Date;
  }
  if (typeof value === 'string' && !isNaN(Date.parse(value))) {
    return new Date(value);
  }
  return null;
};

export const formatDate = (props: {
  formatLocale: string;
  value: any;
  defaultFormattedValue?: string;
}): string => {
  const { formatLocale, value, defaultFormattedValue } = props;
  const dateValue = valueToDate(value);
  return dateValue
    ? new Intl.DateTimeFormat(formatLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(dateValue)
    : defaultFormattedValue
    ? defaultFormattedValue
    : '';
};

export const formatTime = (props: {
  formatLocale: string;
  value: any;
  defaultFormattedValue?: string;
}): string => {
  const { formatLocale, value, defaultFormattedValue } = props;
  const dateValue = valueToDate(value);
  return dateValue
    ? new Intl.DateTimeFormat(formatLocale, {
        hour: 'numeric',
        minute: 'numeric',
      }).format(dateValue)
    : defaultFormattedValue
    ? defaultFormattedValue
    : '';
};
const bogusDateThreshold = -62135596800000;
export const formatDateTime = (props: {
  formatLocale: string;
  value: any;
  defaultFormattedValue?: string;
  useDefaultForBogusDates?: boolean;
}): string => {
  const { formatLocale, value, defaultFormattedValue, useDefaultForBogusDates } = props;
  const dateValue = valueToDate(value);
  if (useDefaultForBogusDates && dateValue && dateValue.getTime() <= bogusDateThreshold) {
    return defaultFormattedValue || '';
  }
  return dateValue
    ? `${formatDate({ formatLocale, value: dateValue, defaultFormattedValue })} ${formatTime({
        formatLocale,
        value: dateValue,
        defaultFormattedValue,
      })}`
    : defaultFormattedValue
    ? defaultFormattedValue
    : '';
};

export const formatText = (text: string | undefined | null, defaultFormattedValue = ''): string => {
  return text || defaultFormattedValue;
};

//  [2021-10-21 TO 2021-10-28]
export const formatDateRangeForSearch = (opts: { startDate: Date; endDate: Date }): string => {
  return `[${formatSingleDateForSearch(opts.startDate)} TO ${formatSingleDateForSearch(
    opts.endDate
  )}]`;
};

// format: 2021-10-21 using local time (we must pass along timezone information)
const formatSingleDateForSearch = (date: Date): string => {
  const timeless = new Date(date.toDateString());
  const day = timeless.getDate();
  const dayStr = day < 10 ? `0${day}` : day;
  const month = timeless.getMonth() + 1; // who uses a zero-based month?
  const monthStr = month < 10 ? `0${month}` : month;
  const year = timeless.getFullYear();
  return `${year}-${monthStr}-${dayStr}`;
};

export const formatNumericRangeForSearch = (opts: {
  start: number;
  end: number;
  valueType: 'integer' | 'float' | 'long';
}): string => {
  if (opts.valueType === 'integer' || opts.valueType === 'long') {
    return `[${opts.start.toFixed(0)} TO ${opts.end.toFixed(0)}]`;
  }
  return `[${opts.start.toFixed(2)} TO ${opts.end.toFixed(2)}]`;
};

export const formatResultsCountRange = (props: {
  localize: Localizer;
  formatLocale: string;
  loadedRowCount: number;
  totalRowCount: number | undefined;
  defaultFormattedValue?: string;
}): string => {
  const { localize, formatLocale, loadedRowCount, totalRowCount, defaultFormattedValue } = props;

  let resultsMessage;

  // making an assumption here that if total row count is undefined, it means we're
  // either still loading results or have filtered results.  but, if loaded row count
  // is also 0, it eliminates the 'have filtered results case' since it doesn't stop
  // getting filtered results until it has a page's worth.
  if (totalRowCount === 0 || (totalRowCount === undefined && loadedRowCount === 0)) {
    resultsMessage = formatNumber({ localize, formatLocale, value: 0 });
  } else {
    if (totalRowCount !== undefined) {
      resultsMessage = localize('search.stats.someresults.label', {
        initial: formatNumber({ localize, formatLocale, value: 1 }),
        loaded: formatNumber({
          localize,
          formatLocale,
          value: loadedRowCount,
          defaultFormattedValue,
        }),
        total: formatNumber({
          localize,
          formatLocale,
          value: totalRowCount,
          defaultFormattedValue,
        }),
      });
    } else {
      resultsMessage = localize('search.stats.results.unknown.total.label', {
        initial: formatNumber({ localize, formatLocale, value: 1 }),
        loaded: formatNumber({
          localize,
          formatLocale,
          value: loadedRowCount,
          defaultFormattedValue,
        }),
      });
    }
  }

  return resultsMessage;
};
